import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Onboarding from './components/Onboarding';
import { useEventStreamInit } from './hooks/useEventStream';

const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const UserRegistrationFlow = lazy(() => import('./pages/user-registration/UserRegistrationFlow'));
const ProductList = lazy(() => import('./pages/ProductList'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const SellerRegistrationFlow = lazy(() => import('./pages/SellerRegistrationFlow'));
const ProductRegister = lazy(() => import('./pages/ProductRegister'));
const SellerDashboard = lazy(() => import('./pages/SellerDashboard'));
const ProductEdit = lazy(() => import('./pages/ProductEdit'));
const Profile = lazy(() => import('./pages/Profile'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const Chat = lazy(() => import('./pages/Chat'));
const Visits = lazy(() => import('./pages/Visits'));
const Reservations = lazy(() => import('./pages/Reservations'));
const Alerts = lazy(() => import('./pages/Alerts'));
const SellerAlerts = lazy(() => import('./pages/SellerAlerts'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PublicTermsPage = lazy(() => import('./pages/policy/PublicTermsPage'));

const ONBOARDING_SHOWN_KEY = 'onboarding_shown';

/** 약관·정책 직접 링크 시 온보딩 없이 본문만 표시 (앱 WebView·스토어 심사용 URL) */
function isPolicyPath(pathname: string): boolean {
  const prefixes = ['/terms', '/privacy', '/location', '/youth'];
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** 공유 링크 /og/products/:id 접속 시 상품 페이지로 리다이렉트 (흰 화면 방지) */
function OgProductRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/products/${id}` : '/'} replace />;
}

function AppGate() {
  const { token, seller, loading: authLoading } = useAuth();
  const isSharingPath = window.location.pathname.startsWith('/products');
  const [showOnboarding, setShowOnboarding] = useState<boolean | null>(null);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // SSE 실시간 연결 초기화
  useEventStreamInit(token, seller?.id);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => setLoadingTimeout(true), 5000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (window.location.pathname.startsWith('/jsadm') ||
      window.location.pathname.startsWith('/admin')) {
      setShowOnboarding(false);
      return;
    }
    if (isSharingPath) {
      setShowOnboarding(false);
      return;
    }
    if (isPolicyPath(window.location.pathname)) {
      setShowOnboarding(false);
      return;
    }
    if (!authLoading) {
      const shown = localStorage.getItem(ONBOARDING_SHOWN_KEY) === 'true';
      if (token || shown) {
        setShowOnboarding(false);
      } else {
        setShowOnboarding(true);
      }
    }
  }, [authLoading, token, isSharingPath]);

  const forceStart = () => {
    setShowOnboarding(false);
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  if (authLoading || showOnboarding === null) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6B35]" />
        <p className="mt-4 text-gray-600 font-medium">데이터를 불러오는 중입니다...</p>
        {loadingTimeout && (
          <button
            type="button"
            onClick={forceStart}
            className="mt-6 px-6 py-3 rounded-lg border border-gray-300 bg-gray-50 text-gray-700 font-semibold hover:bg-gray-100 transition-colors"
          >
            웹 강제 시작하기
          </button>
        )}
      </div>
    );
  }

  if (showOnboarding) {
    return <Onboarding onComplete={handleOnboardingComplete} />;
  }

  return (
    <Router>
      <Suspense fallback={
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6B35]" />
          <p className="mt-4 text-gray-600 text-sm">로딩 중...</p>
        </div>
      }>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<UserRegistrationFlow />} />
          <Route path="/user-registration" element={<UserRegistrationFlow />} />
          <Route path="/seller-registration" element={<SellerRegistrationFlow />} />

          <Route path="/og/products/:id" element={<OgProductRedirect />} />
          <Route path="/" element={<Layout><Home /></Layout>} />
          <Route path="/products" element={<Layout><ProductList /></Layout>} />
          <Route path="/products/:id" element={<Layout><ProductDetail /></Layout>} />

          <Route path="/profile" element={<Layout><ProtectedRoute><Profile /></ProtectedRoute></Layout>} />
          <Route path="/wishlist" element={<Layout><ProtectedRoute><Wishlist /></ProtectedRoute></Layout>} />
          <Route path="/chat" element={<Layout><ProtectedRoute><Chat /></ProtectedRoute></Layout>} />
          <Route path="/visits" element={<Layout><ProtectedRoute><Visits /></ProtectedRoute></Layout>} />
          <Route path="/reservations" element={<Layout><ProtectedRoute><Reservations /></ProtectedRoute></Layout>} />
          <Route path="/alerts" element={<Layout><ProtectedRoute><Alerts /></ProtectedRoute></Layout>} />
          <Route path="/seller-alerts" element={<Layout><ProtectedRoute><SellerAlerts /></ProtectedRoute></Layout>} />

          <Route path="/seller-register" element={<Navigate to="/seller-registration" replace />} />
          <Route path="/seller-dashboard" element={<Layout><ProtectedRoute><SellerDashboard /></ProtectedRoute></Layout>} />
          <Route path="/product-register" element={<Layout><ProtectedRoute><ProductRegister /></ProtectedRoute></Layout>} />
          <Route path="/product-edit/:id" element={<Layout><ProtectedRoute><ProductEdit /></ProtectedRoute></Layout>} />

          {/* 약관/정책 공개 페이지 (앱스토어 등록 및 웹 하단 링크용) */}
          <Route path="/terms" element={<Layout><PublicTermsPage termsKey="service" /></Layout>} />
          <Route path="/privacy" element={<Layout><PublicTermsPage termsKey="privacy" /></Layout>} />
          <Route path="/location" element={<Layout><PublicTermsPage termsKey="location" /></Layout>} />
          <Route path="/youth" element={<Layout><PublicTermsPage termsKey="youth" /></Layout>} />

          <Route path="/admin" element={null} />
          <Route path="/jsadm" element={<AdminDashboard />} />
          <Route path="/jsadm-test" element={<AdminDashboard />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <AppGate />
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
